import { useCallback, useState } from "react";
import { generateMarketingPage } from "@/network/network";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { showToast } from "@/other/utils/utils";
import { getApiErrorMessage } from "@/integrations";
import {
  isAcceptedAsyncGenerateResponse,
  openMarketingAsyncDialog,
} from "@/react/components/entityViews/marketing/utils/asyncGenerateHelpers";
import {
  isSandboxBoardRecord,
  TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG,
} from "@/react/components/entityViews/marketingPages/utils";

export function useMarketingPageGenerate() {
  const { selectedBoard, editorHandler, viewManager } = useSwivuBryntumContext();
  const [generating, setGenerating] = useState(false);
  const boardRecord = selectedBoard?.record as
    | { slug?: string; isSandbox?: boolean }
    | undefined;
  const slug = boardRecord?.slug;
  const isTestDrive = isSandboxBoardRecord(boardRecord);

  const startGenerateForWorkFile = useCallback(
    async (workFileId: number) => {
      if (isTestDrive) {
        showToast({
          msg: TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG,
          type: "error",
          duration: 4000,
        });
        return false;
      }
      if (!slug) {
        showToast({ msg: "No board selected.", type: "error", duration: 3000 });
        return false;
      }
      setGenerating(true);
      try {
        const response = await generateMarketingPage(slug, workFileId);
        if (!isAcceptedAsyncGenerateResponse(response)) {
          showToast({
            msg: "Could not start page generation.",
            type: "error",
            duration: 3000,
          });
          return false;
        }
        openMarketingAsyncDialog(
          editorHandler as { openEditor: (...args: unknown[]) => void },
          "MARKETING_PAGE_GENERATION",
        );
        const pagesView = viewManager?.getDefaultViewByType(VIEW_TYPES.marketingPages);
        if (pagesView) {
          viewManager?.selectView(pagesView);
        }
        return true;
      } catch (err: unknown) {
        showToast({
          msg: getApiErrorMessage(err, "Failed to start publishing your marketing page."),
          type: "error",
          duration: 4000,
        });
        return false;
      } finally {
        setGenerating(false);
      }
    },
    [slug, editorHandler, viewManager, isTestDrive],
  );

  return { generating, isTestDrive, startGenerateForWorkFile };
}
